import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Truck, PackageOpen, Radar, ClipboardList, ShieldCheck, Briefcase, X, Navigation as NavIcon, Car, User, History, Filter, TrendingUp } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { useSubLock, LockNotice } from "@/lib/subLock";
import { YhM3Badge, YhM3PoolActions, YhCallButton } from "@/components/YhModel3Provider";
import { TRANSPORT_REQUEST_STATUS_TR, OFFER_STATUS_TR, EMPTY_RETURN_STATUS_TR, TRANSPORT_STEPS, transportStepIndex, trStatusVariant } from "@/lib/labels";

// Provider-side status transitions (customer confirms COMPLETED)
const NEXT_STATUS = {
  OFFER_ACCEPTED: { key: "AWAITING_PICKUP", label: "Şoför/Araç Ata & Alıma Git" },
  AWAITING_PICKUP: { key: "PICKED_UP", label: "Yükü Aldım" },
  PICKED_UP: { key: "IN_TRANSIT", label: "Taşımaya Başla" },
  IN_TRANSIT: { key: "DELIVERED", label: "Teslim Ettim (Müşteri onaylayacak)" },
};

export default function TransportProvider() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "matched");
  const { tradeLocked, msg: lockMsg } = useSubLock();
  const [matched, setMatched] = useState([]);
  const [pool, setPool] = useState([]);
  const [poolFilters, setPoolFilters] = useState({ from_province: "", to_province: "" });
  const [provinces, setProvinces] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [ers, setErs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [offerFor, setOfferFor] = useState(null); // request id
  const [offerForm, setOfferForm] = useState({ vehicle_id: "", price_tl: "", pickup_at: "" });
  const [erForm, setErForm] = useState({ from_province: "", from_district: "", to_province: "", to_district: "", departure_at: "", vehicle_id: "", capacity_note: "" });
  const [jobDetailId, setJobDetailId] = useState(searchParams.get("job") || null);
  const [matchedFilter, setMatchedFilter] = useState("all"); // all | not_offered | pending | rejected
  // Driver assignment two-step confirmation state
  const [pendingDriverId, setPendingDriverId] = useState("");
  const [changingDriver, setChangingDriver] = useState(false); // 'Şoförü Değiştir' modu

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    if (user.role !== "business_owner") { nav("/"); return; }
    api.get("/locations/provinces").then(({ data }) => setProvinces(data));
    api.get("/my/vehicles").then(({ data }) => setVehicles(data));
    api.get("/my/drivers").then(({ data }) => setDrivers(data)).catch(() => setDrivers([]));
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, nav]);

  const reloadAll = () => {
    api.get("/transport/requests/matched").then(({ data }) => setMatched(data));
    api.get("/transport/my/offers").then(({ data }) => setMyOffers(data));
    api.get("/transport/empty-returns/mine").then(({ data }) => setErs(data));
    api.get("/transport/jobs/active").then(({ data }) => setActiveJobs(data));
    api.get("/transport/jobs/completed").then(({ data }) => setCompletedJobs(data)).catch(() => setCompletedJobs([]));
    api.get("/transport/revenue-stats").then(({ data }) => setRevenue(data)).catch(() => setRevenue(null));
    loadPool();
  };
  const loadPool = () => {
    api.get("/transport/requests/pool", { params: poolFilters }).then(({ data }) => setPool(data));
  };

  const changeTab = (v) => {
    setTab(v);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", v);
    if (v !== "active") sp.delete("job");
    setSearchParams(sp, { replace: true });
    if (v !== "active") setJobDetailId(null);
  };

  const submitOffer = async (e) => {
    e.preventDefault();
    if (!offerForm.vehicle_id || !offerForm.price_tl || !offerForm.pickup_at) return toast.error("Tüm alanları doldurun");
    try {
      await api.post(`/transport/requests/${offerFor}/offers`, {
        vehicle_id: offerForm.vehicle_id,
        price_tl: Number(offerForm.price_tl),
        pickup_at: new Date(offerForm.pickup_at).toISOString(),
      });
      toast.success("Teklif gönderildi");
      setOfferFor(null); setOfferForm({ vehicle_id: "", price_tl: "", pickup_at: "" });
      reloadAll();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const submitEr = async (e) => {
    e.preventDefault();
    if (!erForm.from_province || !erForm.to_province || !erForm.departure_at || !erForm.vehicle_id) return toast.error("Alanları doldurun");
    try {
      await api.post("/transport/empty-returns", { ...erForm, departure_at: new Date(erForm.departure_at).toISOString() });
      toast.success("Boş dönüş yayınlandı");
      setErForm({ from_province: "", from_district: "", to_province: "", to_district: "", departure_at: "", vehicle_id: "", capacity_note: "" });
      reloadAll();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const cancelEr = async (id) => {
    try {
      await api.post(`/transport/empty-returns/${id}/cancel`);
      reloadAll();
    } catch (err) {
      console.warn("[cancelEr]", err?.response?.status || err?.message);
      toast.error(formatErr(err?.response?.data?.detail) || "İlan iptal edilemedi");
    }
  };

  const openJob = (rid) => {
    setJobDetailId(rid);
    setTab("active");
    setPendingDriverId(""); setChangingDriver(false);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", "active"); sp.set("job", rid);
    setSearchParams(sp, { replace: true });
  };
  const closeJob = () => {
    setJobDetailId(null);
    setPendingDriverId(""); setChangingDriver(false);
    const sp = new URLSearchParams(searchParams);
    sp.delete("job");
    setSearchParams(sp, { replace: true });
  };

  // 30s background refresh — silent, no UI reset. Pauses when a modal is open to avoid stomping.
  useEffect(() => {
    if (!user || user.role !== "business_owner") return;
    const id = setInterval(() => {
      // Do NOT refresh when a modal/form is open
      if (offerFor || jobDetailId) return;
      reloadAll();
    }, 30000);
    // Refresh on tab focus
    const onVisible = () => { if (document.visibilityState === "visible" && !offerFor && !jobDetailId) reloadAll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, offerFor, jobDetailId]);

  const advanceStatus = async (rid, next) => {
    try { await api.post(`/transport/requests/${rid}/status`, null, { params: { status: next } }); toast.success("Durum güncellendi"); reloadAll(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const assignDriver = async (rid, driver_id) => {
    try { await api.post(`/transport/requests/${rid}/assign-driver`, null, { params: { driver_id } }); toast.success("Şoför atandı"); reloadAll(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const assignVehicle = async (rid, vehicle_id) => {
    try { await api.post(`/transport/requests/${rid}/assign-vehicle`, null, { params: { vehicle_id } }); toast.success("Araç atandı"); reloadAll(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const currentJob = useMemo(() => activeJobs.find((j) => j.id === jobDetailId), [activeJobs, jobDetailId]);

  const RequestRow = ({ r, providerOffer }) => {
    let statusChip = null;
    if (providerOffer?.status === "pending") statusChip = <Badge className="bg-amber-500 text-white hover:bg-amber-500">Teklif Bekliyor</Badge>;
    else if (providerOffer?.status === "accepted") statusChip = <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">Kabul Edildi</Badge>;
    else if (providerOffer?.status === "rejected") statusChip = <Badge variant="destructive">Reddedildi</Badge>;
    else if (providerOffer?.status === "closed") statusChip = <Badge variant="secondary">Kapandı</Badge>;
    return (
      <div className="rounded-2xl border p-3" data-testid={`tp-req-${r.id}`}>
        <div className="flex items-center justify-between flex-wrap gap-1">
          <div className="text-sm flex items-center gap-1.5 flex-wrap">
            {r.origin === "yolhatti" && <Badge className="bg-[hsl(var(--brand-black))] text-primary hover:bg-[hsl(var(--brand-black))] text-[10px]" data-testid={`tp-yh-badge-${r.id}`}>Yol Hattı İlanı</Badge>}
            {r.yh_model === "model_3" && <YhM3Badge id={r.id} />}
            <span><b>{r.pickup_province}/{r.pickup_district}</b> → <b>{r.delivery_province}/{r.delivery_district}</b></span>
          </div>
          {statusChip || <Badge variant={trStatusVariant(r.status)}>{r.yh_model === "model_3" && r.status === "OPEN" ? "Başvuruya Açık" : TRANSPORT_REQUEST_STATUS_TR[r.status] || r.status}</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">{r.transport_category} · {r.quantity} adet · {r.running_condition}</div>
        <div className="text-xs">Son alım: {new Date(r.deadline_at).toLocaleString("tr-TR")}</div>
        {r.match_reasons && <div className="flex flex-wrap gap-1 mt-1">{r.match_reasons.map((m, i) => <Badge key={`${m}-${i}`} variant="secondary" className="text-xs">{m}</Badge>)}</div>}
        {r.yh_model === "model_3" && <YhM3PoolActions r={r} onChanged={reloadAll} />}
        {!providerOffer && r.yh_model !== "model_3" && <Button size="sm" className="mt-2 rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold" onClick={() => setOfferFor(r.id)} data-testid={`tp-offer-btn-${r.id}`}>Teklif Ver</Button>}
        {providerOffer && providerOffer.status === "pending" && <div className="mt-2 text-xs text-amber-600 font-semibold">Teklifiniz: {Number(providerOffer.price_tl).toLocaleString("tr-TR")} TL</div>}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      {/* Branded header */}
      <div className="relative overflow-hidden rounded-2xl mb-4 bg-[hsl(var(--brand-black))] p-5 sm:p-6">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FFC107 0px, transparent 120px), radial-gradient(circle at 85% 80%, #FFC107 0px, transparent 160px)" }} />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
            <Radar className="w-6 h-6 text-primary" />
          </div>
          <div className="text-white">
            <div className="flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest">
              <ShieldCheck className="w-3 h-3" /> YOL HATTI · SAĞLAYICI PANELİ
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight">Taşıma Pazarı</h1>
            <p className="text-xs text-white/70 mt-1">Yakınınızdaki taşıma taleplerini görün, teklif verin, aktif işlerinizi yönetin.</p>
          </div>
        </div>
      </div>

      {/* Quick stats — clickable */}
      <div className="grid grid-cols-4 gap-2 mb-4 text-center text-xs">
        <button onClick={() => changeTab("matched")} className="rounded-2xl border p-3 hover:border-primary/60 transition-colors" data-testid="tp-stat-card-matched">
          <div className="text-primary font-extrabold text-xl leading-none" data-testid="tp-stat-matched">{matched.length}</div>
          <div className="text-muted-foreground mt-1">Bana Gelen</div>
        </button>
        <button onClick={() => changeTab("offers")} className="rounded-2xl border p-3 hover:border-primary/60 transition-colors" data-testid="tp-stat-card-offers">
          <div className="text-primary font-extrabold text-xl leading-none" data-testid="tp-stat-offers">{myOffers.filter((o) => o.status === "pending").length}</div>
          <div className="text-muted-foreground mt-1">Bekleyen Teklif</div>
        </button>
        <button onClick={() => changeTab("active")} className="rounded-2xl border p-3 border-primary/40 hover:border-primary transition-colors" data-testid="tp-stat-card-active">
          <div className="text-primary font-extrabold text-xl leading-none" data-testid="tp-stat-active">{activeJobs.length}</div>
          <div className="text-muted-foreground mt-1">Aktif İş</div>
        </button>
        <button onClick={() => changeTab("ers")} className="rounded-2xl border p-3 hover:border-primary/60 transition-colors" data-testid="tp-stat-card-er">
          <div className="text-primary font-extrabold text-xl leading-none" data-testid="tp-stat-er">{ers.filter((e) => e.status === "ACTIVE").length}</div>
          <div className="text-muted-foreground mt-1">Aktif Boş Dönüş</div>
        </button>
      </div>

      <SubscriptionBanner />

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="matched" data-testid="tp-tab-matched"><Radar className="w-4 h-4 mr-1" /> Bana Gelen İşler</TabsTrigger>
          <TabsTrigger value="pool" data-testid="tp-tab-pool"><ClipboardList className="w-4 h-4 mr-1" /> İş Havuzu</TabsTrigger>
          <TabsTrigger value="offers" data-testid="tp-tab-offers"><Truck className="w-4 h-4 mr-1" /> Tekliflerim</TabsTrigger>
          <TabsTrigger value="active" data-testid="tp-tab-active"><Briefcase className="w-4 h-4 mr-1" /> Aktif İşler {activeJobs.length > 0 && <Badge className="ml-1 bg-primary text-[hsl(var(--brand-black))]">{activeJobs.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="history" data-testid="tp-tab-history"><History className="w-4 h-4 mr-1" /> Tamamlanan İşler</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tp-tab-revenue"><TrendingUp className="w-4 h-4 mr-1" /> Yol Hattı Cirosu</TabsTrigger>
          <TabsTrigger value="ers" data-testid="tp-tab-ers"><PackageOpen className="w-4 h-4 mr-1" /> Boş Dönüşlerim</TabsTrigger>
        </TabsList>

        <TabsContent value="matched">
          <div className="flex items-center gap-2 mb-3 text-xs flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">Filtre:</span>
            {[
              { k: "all", label: "Tümü" },
              { k: "not_offered", label: "Teklif Verilmemiş" },
              { k: "pending", label: "Teklif Bekleyen" },
              { k: "rejected", label: "Reddedilen" },
            ].map((opt) => (
              <button key={opt.k} onClick={() => setMatchedFilter(opt.k)} data-testid={`tp-filter-${opt.k}`} className={`px-3 py-1 rounded-full border text-xs font-medium ${matchedFilter === opt.k ? "bg-primary text-[hsl(var(--brand-black))] border-primary" : "border-border hover:border-primary/40"}`}>{opt.label}</button>
            ))}
          </div>
          <div className="grid gap-2">
            {(() => {
              const offerByReq = Object.fromEntries(myOffers.map((o) => [o.request_id, o]));
              const filtered = matched.filter((r) => {
                const o = offerByReq[r.id];
                if (matchedFilter === "all") return true;
                if (matchedFilter === "not_offered") return !o;
                if (matchedFilter === "pending") return o?.status === "pending";
                if (matchedFilter === "rejected") return o?.status === "rejected";
                return true;
              });
              if (filtered.length === 0) return (
                <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                  <Radar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  {matched.length === 0 ? "Otomatik eşleşme yok. İş Havuzu sekmesinden manuel arayabilirsiniz." : "Bu filtreye uygun iş yok."}
                </div>
              );
              return filtered.map((r) => <RequestRow key={r.id} r={r} providerOffer={offerByReq[r.id]} />);
            })()}
          </div>
        </TabsContent>

        <TabsContent value="pool">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <SearchableSelect testid="tp-from" value={poolFilters.from_province} onChange={(v) => setPoolFilters((s) => ({ ...s, from_province: v }))} options={["", ...provinces]} placeholder="Nereden" />
            <SearchableSelect testid="tp-to" value={poolFilters.to_province} onChange={(v) => setPoolFilters((s) => ({ ...s, to_province: v }))} options={["", ...provinces]} placeholder="Nereye" />
            <Button onClick={loadPool} data-testid="tp-search" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">Ara</Button>
          </div>
          <div className="grid gap-2">
            {pool.length === 0 && (
              <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Sonuç yok. Filtreleri değiştirip tekrar arayın.
              </div>
            )}
            {pool.map((r) => <RequestRow key={r.id} r={r} />)}
          </div>
        </TabsContent>

        <TabsContent value="offers">
          <div className="grid gap-2">
            {myOffers.length === 0 && (
              <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Henüz teklifiniz yok.
              </div>
            )}
            {myOffers.map((o) => {
              const isAccepted = o.status === "accepted";
              const isPending = o.status === "pending";
              const withdraw = async () => {
                if (!window.confirm("Bu teklifi geri çekmek istediğinize emin misiniz?")) return;
                try { await api.post(`/transport/offers/${o.id}/withdraw`); toast.success("Teklif geri çekildi"); reloadAll(); }
                catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
              };
              return (
                <div key={o.id} className="rounded-2xl border p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`tp-offer-row-${o.id}`}>
                  <div>
                    <div className="font-semibold text-sm">{Number(o.price_tl).toLocaleString("tr-TR")} TL · {o.vehicle_type}</div>
                    <div className="text-xs text-muted-foreground">Alım: {new Date(o.pickup_at).toLocaleString("tr-TR")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={trStatusVariant(o.status)}>{OFFER_STATUS_TR[o.status] || o.status}</Badge>
                    {isPending && (
                      <Button size="sm" variant="outline" onClick={withdraw} data-testid={`tp-withdraw-${o.id}`} className="rounded-full">
                        Teklifi Geri Çek
                      </Button>
                    )}
                    {isAccepted && (
                      <Button size="sm" onClick={() => openJob(o.request_id)} data-testid={`tp-goto-job-${o.id}`} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90">
                        <Briefcase className="w-4 h-4 mr-1" /> İşi Gör
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="active">
          <div className="grid gap-2">
            {activeJobs.length === 0 && (
              <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Aktif işiniz yok. Teklifleriniz kabul edildiğinde burada görünür.
              </div>
            )}
            {activeJobs.map((j) => (
              <button
                key={j.id}
                onClick={() => openJob(j.id)}
                data-testid={`tp-active-${j.id}`}
                className="text-left rounded-2xl border-2 border-primary/40 bg-primary/5 p-3 hover:border-primary transition-colors"
              >
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="text-sm font-semibold"><b>{j.pickup_province}/{j.pickup_district}</b> → <b>{j.delivery_province}/{j.delivery_district}</b></div>
                  <Badge variant={trStatusVariant(j.status)}>{TRANSPORT_REQUEST_STATUS_TR[j.status] || j.status}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{j.transport_category} · {j.quantity} adet</div>
                <div className="text-xs">Anlaşılan: <span className="text-primary font-bold">{Number(j.accepted_price_tl || 0).toLocaleString("tr-TR")} TL</span></div>
                {j.assigned_driver && <div className="text-xs text-muted-foreground">Şoför: {j.assigned_driver.name}</div>}
                <div className="text-xs text-primary mt-1 font-semibold">Detay & Durum Güncelle →</div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="grid gap-2">
            {completedJobs.length === 0 && (
              <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Henüz tamamlanan iş yok.
              </div>
            )}
            {completedJobs.map((j) => (
              <div key={j.id} className="rounded-2xl border p-3" data-testid={`tp-history-${j.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="text-sm font-semibold"><b>{j.pickup_province}/{j.pickup_district}</b> → <b>{j.delivery_province}/{j.delivery_district}</b></div>
                  <Badge variant={trStatusVariant(j.status)}>{TRANSPORT_REQUEST_STATUS_TR[j.status] || j.status}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{j.transport_category} · {j.quantity} adet</div>
                <div className="text-xs">Anlaşılan: <span className="text-primary font-bold">{Number(j.accepted_price_tl || 0).toLocaleString("tr-TR")} TL</span></div>
                {j.assigned_vehicle && <div className="text-xs text-muted-foreground">Araç: {j.assigned_vehicle.name}</div>}
                {j.assigned_driver && <div className="text-xs text-muted-foreground">Şoför: {j.assigned_driver.name}</div>}
                <div className="text-xs text-muted-foreground">Kabul: {j.accepted_at ? new Date(j.accepted_at).toLocaleString("tr-TR") : "—"}</div>
                <div className="text-xs text-muted-foreground">Tamamlanma: {j.completed_at ? new Date(j.completed_at).toLocaleString("tr-TR") : "—"}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="revenue">
          {!revenue && <div className="rounded-2xl border p-6 text-center text-sm text-muted-foreground">Yükleniyor…</div>}
          {revenue && (
            <div className="space-y-4">
              <div className="rounded-2xl bg-[hsl(var(--brand-black))] text-white p-4">
                <div className="text-primary text-[10px] font-bold tracking-widest flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> YOL HATTI ÜZERİNDEN OLUŞAN CİRO</div>
                <div className="text-xs text-white/60 mt-1">Sadece Yol Hattı üzerinden gelen ve TAMAMLANMIŞ işlerin anlaşılan brüt tutarları. Kâr değildir.</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { key: "today", label: "Bugün" },
                  { key: "month", label: "Bu Ay" },
                  { key: "year", label: "Bu Yıl" },
                  { key: "total", label: "Toplam" },
                ].map((b) => (
                  <div key={b.key} className="rounded-2xl border p-3" data-testid={`tp-rev-${b.key}`}>
                    <div className="text-xs text-muted-foreground">{b.label}</div>
                    <div className="text-primary text-xl font-extrabold mt-1">{Number(revenue.stats[b.key].amount || 0).toLocaleString("tr-TR")} TL</div>
                    <div className="text-[10px] text-muted-foreground">{revenue.stats[b.key].count} iş</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">İş Kalemleri ({revenue.items.length})</div>
                <div className="grid gap-2">
                  {revenue.items.length === 0 && <div className="rounded-2xl border border-dashed p-4 text-center text-sm text-muted-foreground">Henüz tamamlanan iş yok.</div>}
                  {revenue.items.map((it) => (
                    <div key={it.id} className="rounded-2xl border p-3 flex items-center justify-between gap-2" data-testid={`tp-rev-item-${it.id}`}>
                      <div className="min-w-0">
                        {it.kind === "roadside" ? (
                          <>
                            <div className="text-sm font-semibold truncate">
                              <Badge variant="secondary" className="mr-1">Acil Yol Yardım</Badge>
                              {it.request_type || "—"}{it.customer_vehicle_type ? ` · ${it.customer_vehicle_type}` : ""}
                            </div>
                            <div className="text-xs text-muted-foreground">{it.completed_at ? new Date(it.completed_at).toLocaleString("tr-TR") : "—"}</div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm font-semibold truncate">{it.pickup} → {it.delivery}</div>
                            <div className="text-xs text-muted-foreground">{it.category} · {it.completed_at ? new Date(it.completed_at).toLocaleString("tr-TR") : "—"}</div>
                          </>
                        )}
                      </div>
                      <div className="text-primary font-bold shrink-0">{Number(it.amount).toLocaleString("tr-TR")} TL</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="ers">
          <form onSubmit={submitEr} className="rounded-2xl border p-3 mb-3 space-y-2">
            {tradeLocked && <LockNotice msg={lockMsg} testid="er-lock-notice" />}
            <h3 className="font-semibold text-sm">Yeni Boş Dönüş</h3>
            <div className="grid grid-cols-2 gap-2">
              <SearchableSelect testid="er-from" value={erForm.from_province} onChange={(v) => setErForm({ ...erForm, from_province: v })} options={provinces} placeholder="Nereden İl" />
              <SearchableSelect testid="er-to" value={erForm.to_province} onChange={(v) => setErForm({ ...erForm, to_province: v })} options={provinces} placeholder="Nereye İl" />
            </div>
            <Input type="datetime-local" value={erForm.departure_at} onChange={(e) => setErForm({ ...erForm, departure_at: e.target.value })} data-testid="er-dep" />
            <Select value={erForm.vehicle_id} onValueChange={(v) => setErForm({ ...erForm, vehicle_id: v })}>
              <SelectTrigger data-testid="er-vehicle"><SelectValue placeholder="Araç" /></SelectTrigger>
              <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · {v.vehicle_type}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Kapasite notu (telefon/iletişim yazmayın)" value={erForm.capacity_note} onChange={(e) => setErForm({ ...erForm, capacity_note: e.target.value })} data-testid="er-note" />
            <Button type="submit" size="sm" data-testid="er-submit" disabled={tradeLocked} title={tradeLocked ? lockMsg : undefined} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">Yayınla</Button>
          </form>
          <div className="grid gap-2">
            {ers.map((e) => (
              <div key={e.id} className="rounded-2xl border p-3 flex items-center justify-between gap-2">
                <div><div className="text-sm"><b>{e.from_province}</b> → <b>{e.to_province}</b></div>
                  <div className="text-xs text-muted-foreground">{new Date(e.departure_at).toLocaleString("tr-TR")} · <Badge variant={trStatusVariant(e.status)}>{EMPTY_RETURN_STATUS_TR[e.status] || e.status}</Badge></div></div>
                {e.status === "ACTIVE" && <Button size="sm" variant="ghost" onClick={() => cancelEr(e.id)}>İptal</Button>}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* ================= OFFER SUBMIT MODAL ================= */}
      {offerFor && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4">
          <form onSubmit={submitOffer} className="bg-card rounded-2xl p-4 w-full max-w-md space-y-2">
            {tradeLocked && <LockNotice msg={lockMsg} testid="of-lock-notice" />}
            <h3 className="font-semibold">Teklif Ver</h3>
            <Select value={offerForm.vehicle_id} onValueChange={(v) => setOfferForm({ ...offerForm, vehicle_id: v })}>
              <SelectTrigger data-testid="of-vehicle"><SelectValue placeholder="Aracınız" /></SelectTrigger>
              <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · {v.vehicle_type} · {v.capacity || "—"}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Teklif tutarı (TL)" value={offerForm.price_tl} onChange={(e) => setOfferForm({ ...offerForm, price_tl: e.target.value })} data-testid="of-price" />
            <Input type="datetime-local" value={offerForm.pickup_at} onChange={(e) => setOfferForm({ ...offerForm, pickup_at: e.target.value })} data-testid="of-pickup" />
            <div className="text-xs text-muted-foreground">Serbest metin alanı yok. İletişim bilgisi paylaşmak yasaktır.</div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setOfferFor(null)} className="rounded-full">Vazgeç</Button>
              <Button type="submit" data-testid="of-submit" disabled={tradeLocked} title={tradeLocked ? lockMsg : undefined} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">Gönder</Button>
            </div>
          </form>
        </div>
      )}

      {/* ================= ACTIVE JOB DETAIL MODAL ================= */}
      {currentJob && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-3 sm:p-4">
          <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="tp-job-detail">
            <div className="sticky top-0 z-10 bg-card border-b p-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">Aktif İş</div>
                <div className="text-lg font-extrabold truncate">{currentJob.pickup_province}/{currentJob.pickup_district} → {currentJob.delivery_province}/{currentJob.delivery_district}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={closeJob} data-testid="tp-job-close"><X className="w-5 h-5" /></Button>
            </div>

            <div className="p-4 space-y-4">
              {/* Status timeline */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Süreç</div>
                <div className="flex items-center gap-1 overflow-x-auto pb-1">
                  {TRANSPORT_STEPS.filter((s) => s.key !== "OPEN").map((s, i, arr) => {
                    const currentIdx = transportStepIndex(currentJob.status);
                    const myIdx = TRANSPORT_STEPS.findIndex((x) => x.key === s.key);
                    const done = myIdx <= currentIdx;
                    return (
                      <div key={s.key} className="flex items-center shrink-0">
                        <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold ${done ? "bg-primary text-[hsl(var(--brand-black))]" : "bg-muted text-muted-foreground"}`}>{myIdx + 1}</div>
                        <div className={`ml-1 text-[10px] font-medium ${done ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</div>
                        {i < arr.length - 1 && <div className={`w-3 h-0.5 mx-1 ${done ? "bg-primary" : "bg-muted"}`} />}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1"><Badge variant={trStatusVariant(currentJob.status)}>{TRANSPORT_REQUEST_STATUS_TR[currentJob.status] || currentJob.status}</Badge></div>
              </div>

              {currentJob.yh_contact && (
                <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="tp-job-m3-contact">
                  <div className="text-sm"><div className="text-xs text-muted-foreground">Müşteri</div><b>{currentJob.yh_contact.customer_name}</b></div>
                  <YhCallButton phone={currentJob.yh_contact.phone} testid="tp-job-m3-call" />
                </div>
              )}

              {/* Full addresses (unlocked to accepted provider only) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Alım (kesin adres)</div>
                  <div className="font-semibold">{currentJob.pickup_province}/{currentJob.pickup_district}</div>
                  <div className="text-muted-foreground">{currentJob.pickup_address || "—"}</div>
                  {currentJob.pickup_lat != null && (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${currentJob.pickup_lat},${currentJob.pickup_lng}`} target="_blank" rel="noopener noreferrer" className="text-primary text-xs inline-flex items-center gap-1 mt-1"><NavIcon className="w-3 h-3" /> Yol Tarifi</a>
                  )}
                </div>
                <div className="rounded-xl border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Teslim (kesin adres)</div>
                  <div className="font-semibold">{currentJob.delivery_province}/{currentJob.delivery_district}</div>
                  <div className="text-muted-foreground">{currentJob.delivery_address || "—"}</div>
                  {currentJob.delivery_lat != null && (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${currentJob.delivery_lat},${currentJob.delivery_lng}`} target="_blank" rel="noopener noreferrer" className="text-primary text-xs inline-flex items-center gap-1 mt-1"><NavIcon className="w-3 h-3" /> Yol Tarifi</a>
                  )}
                </div>
                <div className="rounded-xl border p-2 sm:col-span-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Yük</div>
                  <div>{currentJob.transport_category} · {currentJob.quantity} adet · {currentJob.running_condition} {currentJob.make_model ? `· ${currentJob.make_model}` : ""}</div>
                  <div className="text-primary font-bold mt-1" data-testid="tp-job-price">Anlaşılan: {Number(currentJob.accepted_price_tl || 0).toLocaleString("tr-TR")} TL</div>
                  {currentJob.description && <div className="text-muted-foreground mt-1">{currentJob.description}</div>}
                </div>
              </div>

              {/* Vehicle / Driver assignment */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="rounded-xl border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Car className="w-3 h-3" /> Araç</div>
                  <Select value={currentJob.assigned_vehicle_id || ""} onValueChange={(v) => assignVehicle(currentJob.id, v)}>
                    <SelectTrigger data-testid="tp-job-vehicle"><SelectValue placeholder="Araç seç" /></SelectTrigger>
                    <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · {v.vehicle_type}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="rounded-xl border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Şoför</div>
                  {/* Show current assigned driver as read-only unless in "change" mode */}
                  {currentJob.assigned_driver_id && !changingDriver ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold truncate" data-testid="tp-job-current-driver">
                        {currentJob.assigned_driver?.name || (drivers.find((d) => d.id === currentJob.assigned_driver_id)?.name) || "Atandı"}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => { setChangingDriver(true); setPendingDriverId(""); }} data-testid="tp-change-driver-btn" className="rounded-full">
                        Şoförü Değiştir
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Select value={pendingDriverId} onValueChange={setPendingDriverId} disabled={drivers.length === 0}>
                        <SelectTrigger data-testid="tp-job-driver"><SelectValue placeholder={drivers.length === 0 ? "Şoförünüz yok" : "Şoför seç"} /></SelectTrigger>
                        <SelectContent>
                          {drivers
                            .filter((d) => d.id !== currentJob.assigned_driver_id)
                            .map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2 mt-2">
                        {pendingDriverId && (
                          <Button size="sm" onClick={async () => { await assignDriver(currentJob.id, pendingDriverId); setPendingDriverId(""); setChangingDriver(false); }} data-testid="tp-confirm-driver" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold flex-1">
                            {currentJob.assigned_driver_id ? "Değişikliği Onayla" : "Şoförü Ata"}
                          </Button>
                        )}
                        {changingDriver && (
                          <Button size="sm" variant="ghost" onClick={() => { setChangingDriver(false); setPendingDriverId(""); }} className="rounded-full">Vazgeç</Button>
                        )}
                      </div>
                      {!pendingDriverId && !changingDriver && drivers.length > 0 && <div className="text-[11px] text-muted-foreground mt-1">Şoför seçin ve <b>Şoförü Ata</b> butonuyla onaylayın. Seçim tek başına atama yapmaz.</div>}
                    </>
                  )}
                  {/* Driver history */}
                  {currentJob.driver_history && currentJob.driver_history.length > 0 && (
                    <div className="mt-2 text-[10px] text-muted-foreground space-y-0.5" data-testid="tp-driver-history">
                      {currentJob.driver_history.slice(-3).map((h, i) => {
                        const oldName = drivers.find((d) => d.id === h.old_driver_id)?.name;
                        const newName = drivers.find((d) => d.id === h.new_driver_id)?.name;
                        return <div key={`${h.at}-${h.event}-${i}`}>{h.event === "DRIVER_REASSIGNED" ? `Şoför değiştirildi: ${oldName || "—"} → ${newName || "—"}` : `Şoför atandı: ${newName || "—"}`} · {new Date(h.at).toLocaleString("tr-TR")}</div>;
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Next action */}
              {NEXT_STATUS[currentJob.status] && (
                <Button
                  className="w-full rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold"
                  data-testid="tp-job-advance"
                  onClick={() => advanceStatus(currentJob.id, NEXT_STATUS[currentJob.status].key)}
                >
                  {NEXT_STATUS[currentJob.status].label}
                </Button>
              )}
              {currentJob.status === "COMPLETED" && (
                <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/40 p-3 text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Bu iş tamamlandı. Aktif İşler listesinden çıkarılacak.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
